import React, { useState, useEffect } from 'react';
import { Table, Input, Divider, Image, InputNumber } from 'antd';
import 'antd/dist/antd.css';
import { useTranslation } from 'react-i18next';
import { SearchOutlined } from '@ant-design/icons';

const Couriers = ({ couriers, setSelectedCouriers }) => {
  const [searchText, setSearchText] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [selectedCourierCount, setSelectedCourierCount] = useState(0);
  const [selectedCourierIds, setSelectedCourierIds] = useState([]);
  const [totalCourierCount, setTotalCourierCount] = useState(0);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const { t } = useTranslation();
  const [delayInputs, setDelayInputs] = useState({});

  useEffect(() => {
    setFilteredData(
      couriers.map((courier) => ({ ...courier, isSelected: false })),
    );
    setTotalCourierCount(couriers.length);
  }, [couriers]);

  useEffect(() => {
    setSelectedRowKeys(selectedCourierIds);
    setSelectedCourierCount(selectedCourierIds.length);
    setTotalCourierCount(filteredData.length);
    const updatedFilteredData = filteredData.map((courier) => {
      const delay = delayInputs[courier.courierId];
      if (delay !== undefined) {
        return { ...courier, delay };
      }
      return courier;
    });

    setSelectedCouriers(
      updatedFilteredData.filter((courier) => courier.isSelected),
    );
  }, [filteredData]);

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchText(value);

    const filtered = couriers.filter((record) =>
      Object.keys(record).some((key) =>
        record[key]?.toString()?.toLowerCase()?.includes(value?.toLowerCase()),
      ),
    );

    setFilteredData(
      filtered.map((courier) => ({
        ...courier,
        isSelected: selectedRowKeys.includes(courier.courierId),
      })),
    );

    setSelectedCouriers(filtered.filter((courier) => courier.isSelected));
  };
  const onSelect = (record) => {
    if (selectedRowKeys.includes(record.courierId)) {
      const updatedRowKeys = selectedRowKeys.filter(
        (id) => id !== record.courierId,
      );
      setSelectedRowKeys(updatedRowKeys);

      if (selectedCourierIds.includes(record.courierId)) {
        setSelectedCourierIds((prevSelectedIds) =>
          prevSelectedIds.filter((id) => id !== record.courierId),
        );
      }
    } else {
      const updatedRowKeys = [...selectedRowKeys, record.courierId];
      setSelectedRowKeys(updatedRowKeys);

      if (!selectedCourierIds.includes(record.courierId)) {
        setSelectedCourierIds((prevSelectedIds) => [
          ...prevSelectedIds,
          record.courierId,
        ]);
      }
    }
  };

  const onSelectCancel = (record) => {
    const updatedRowKeys = selectedRowKeys.filter(
      (id) => id !== record.courierId,
    );
    setSelectedRowKeys(updatedRowKeys);

    if (selectedCourierIds.includes(record.courierId)) {
      setSelectedCourierIds((prevSelectedIds) =>
        prevSelectedIds.filter((id) => id !== record.courierId),
      );
    }
  };

  const handleDelayChange = (e, record) => {
    const value = e ? e : 0;
    const courierId = record.courierId;

    setDelayInputs((prevDelayInputs) => ({
      ...prevDelayInputs,
      [courierId]: value,
    }));

    const newData = [...filteredData];
    const dataIndex = newData.findIndex((item) => item.courierId === courierId);
    if (dataIndex !== -1) {
      newData[dataIndex] = { ...newData[dataIndex], delay: value };
      setFilteredData(newData);
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onSelect: onSelect,
    onSelectCancel: onSelectCancel,
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedRowKeys(selectedRowKeys);
      setSelectedCourierCount(selectedRowKeys.length);
      setSelectedCouriers(
        selectedRows.filter((courier) =>
          selectedRowKeys.includes(courier.courierId),
        ),
      );
    },
  };

  const columns = [
    {
      title: t('selectAll'),
      dataIndex: 'imageUrl',
      width: '70px',
      render: (imageUrl) => (
        <Image
          src={imageUrl ? imageUrl : '/user.svg'}
          width={40}
          height={40}
          alt="Courier"
        />
      ),
    },
    {
      title: '',
      dataIndex: 'nameSurname',
      width: '150px',
      fontSize: '20px',
      render: (text, record) => (
        <>
          <div>{text}</div>
          <div>{record.plateNumber}</div>
        </>
      ),
    },
    {
      title: t('delay'),
      dataIndex: 'delay',
      width: '75px',
      render: (text, record) => (
        <InputNumber
          value={delayInputs[record.courierId] || text}
          placeholder="0 DK"
          onChange={(e) => handleDelayChange(e, record)}
          style={{ width: '100%' }}
          min={0}
        />
      ),
    },
  ];

  return (
    <div>
      <Divider style={{ margin: '12px 0px' }} />
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-around',
        }}
      >
        <Input
          placeholder={t('courierSearch')}
          onChange={handleSearch}
          style={{ width: '30%', borderRadius: '5px' }}
          suffix={<SearchOutlined />}
        />
        <span style={{ fontSize: '12px', marginLeft: '5px' }}>
          {t('selectedCourierCount')} : {selectedCourierCount}
        </span>
        <span style={{ fontSize: '12px', margin: '2px' }}>
          {t('totalCourierCountForRouting')} : {totalCourierCount}
        </span>
      </div>
      <Divider style={{ margin: '12px 0px' }} />
      <Table
        columns={columns}
        dataSource={filteredData}
        pagination={false}
        showHeader={true}
        rowSelection={rowSelection}
        rowKey="courierId"
        size="small"
        style={{ fontSize: 'small' }}
        scroll={{ y: '64vh' }}
      />
    </div>
  );
};

export default Couriers;
